import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Flex,
  Alert,
  AlertIcon,
  Box,
} from '@chakra-ui/react';
import queryString from 'query-string';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { BrowserQRCodeReader } from '@zxing/library';

import PageBanner from '../../components/pageBanner/PageBanner';
import PageContainer from '../../components/pageContainer/PageContainer';
import { verifyCert } from '../../slice/verifySlice';

import './Verify.css';

function Verify() {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const history = useHistory();

  const params = queryString.parse(search);
  const [certificateId, setCertificateId] = useState(params.certId || '');
  const [isVerifying, setIsVerifying] = useState(false);

  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [coords, setCoords] = useState(null);
  const [useIpLocation, setUseIpLocation] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);

  const [showScanner, setShowScanner] = useState(false);

  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const isProcessingRef = useRef(false);

  /* ---------------- Location ---------------- */

  useEffect(() => {
    if (!navigator.geolocation) {
      setUseIpLocation(true);
      fetchIpLocation();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
        setCoords(pos.coords);
        setIsLoadingLocation(false);
      },
      () => {
        setUseIpLocation(true);
        fetchIpLocation();
      }
    );
  }, []);

  const fetchIpLocation = async () => {
    try {
      const res = await fetch('https://ipapi.co/json/');
      const data = await res.json();
      if (data.latitude && data.longitude) {
        setLatitude(data.latitude);
        setLongitude(data.longitude);
        setCoords({ latitude: data.latitude, longitude: data.longitude });
      }
    } finally {
      setIsLoadingLocation(false);
    }
  };

  /* ---------------- QR Scanner ---------------- */

  useEffect(() => {
    if (showScanner && videoRef.current) startScanner();
    return stopScanner;
  }, [showScanner]);

  const startScanner = async () => {
    codeReaderRef.current = new BrowserQRCodeReader();
    codeReaderRef.current.decodeFromVideoDevice(
      undefined,
      videoRef.current,
      (result) => {
        if (result && !isProcessingRef.current) {
          handleQrScan(result.getText());
        }
      }
    );
  };

  const stopScanner = () => {
    codeReaderRef.current?.reset();
    codeReaderRef.current = null;
    isProcessingRef.current = false;
  };

  const handleQrScan = async text => {
    const parsed = queryString.parseUrl(text);
    const certId =
      parsed.query?.certId || parsed.query?.certificateId;

    if (!certId) return;

    isProcessingRef.current = true;
    stopScanner();
    setShowScanner(false);
    setCertificateId(certId);

    await handleVerify(certId);
  };

  /* ---------------- Verification ---------------- */

  const handleVerify = async certId => {
    if (isVerifying) return;

    setIsVerifying(true);

    const res = await dispatch(
      verifyCert({
        certificateId: certId,
        latitude,
        longitude,
        useIpLocation,
      })
    );

    setIsVerifying(false);
    isProcessingRef.current = false;

    if (!res.payload?.ok) {
      history.push('/verify-result', {
        certificateId: certId,
        error: res.payload?.error || 'Verification failed',
      });
      return;
    }
    
    history.push('/verify-result', {
      certificateId: certId,
      body: res.payload?.body,
    });
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!certificateId) return;
    handleVerify(certificateId);
  };

  /* ---------------- Render ---------------- */

  return (
    <PageContainer>
      <PageBanner />

      {isLoadingLocation && (
        <Alert status="info" mb={4}>
          <AlertIcon />
          Getting location information…
        </Alert>
      )}

      {!isLoadingLocation && (
        <form onSubmit={handleSubmit}>
          <FormControl mb={3}>
            <FormLabel>Certificate ID</FormLabel>
            <Input
              value={certificateId}
              onChange={e => setCertificateId(e.target.value)}
              width="320px"
            />
          </FormControl>

          <Flex gap={2} width="320px">
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={isVerifying}
              flex="1"
            >
              Verify
            </Button>

            <Button
              colorScheme="green"
              flex="1"
              onClick={() => setShowScanner(true)}
            >
              Scan QR
            </Button>
          </Flex>
        </form>
      )}

      {showScanner && (
        <Box mt={6} textAlign="center">
          <video ref={videoRef} style={{ width: '100%' }} />
          <Button mt={3} colorScheme="red" onClick={stopScanner}>
            Close Scanner
          </Button>
        </Box>
      )}
    </PageContainer>
  );
}

export default Verify;
